import React, { useMemo, useState } from "react";
import { Plus, X, Search, Check } from "lucide-react";
import IntegrationIcon from "@/components/IntegrationIcon";
import IntegrationLogo from "@/components/IntegrationLogo";

/**
 * IntegrationPickerModal
 * Browses the full integrations catalog (grouped by category) and lets the
 * user pick one to authorize. Highlights the worker's "recommended" set.
 */
export default function IntegrationPickerModal({
    catalog,           // {integrations[], categories[]}
    recommendedIds,    // array of integration ids recommended for this worker
    connectedIds,      // Set<integration_id> already connected
    onSelect,          // (integration) => void
    onClose,
}) {
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("recommended");

    const recSet = useMemo(() => new Set(recommendedIds || []), [recommendedIds]);
    const conSet = connectedIds instanceof Set ? connectedIds : new Set(connectedIds || []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        let base = catalog?.integrations || [];
        if (cat === "recommended") base = base.filter((i) => recSet.has(i.id));
        else if (cat !== "all") base = base.filter((i) => i.category === cat);
        if (term) base = base.filter((i) =>
            i.name.toLowerCase().includes(term) ||
            i.category.toLowerCase().includes(term) ||
            (i.description || "").toLowerCase().includes(term)
        );
        return base;
    }, [catalog, cat, q, recSet]);

    const allCount = catalog?.integrations?.length || 0;
    const catCounts = useMemo(() => {
        const out = {};
        for (const c of catalog?.categories || []) {
            out[c.key] = (catalog?.integrations || []).filter((i) => i.category === c.key).length;
        }
        return out;
    }, [catalog]);

    return (
        <div
            className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4"
            data-testid="int-picker-modal"
        >
            <div className="wfos-card w-full max-w-[720px] max-h-[80vh] flex flex-col overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="px-6 pt-5 pb-4 border-b border-[#e8e8e3] flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="text-lg font-semibold text-gray-900">Add an integration</div>
                        <div className="text-xs text-gray-500">Give this worker access to the tools they live in.</div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="int-picker-close"
                        className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-[#f4f4ef]"
                    >
                        <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                </div>

                {/* Filters row */}
                <div className="px-6 py-3 border-b border-[#e8e8e3] flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="int-picker-search"
                            placeholder="Search 600+ tools…"
                            className="w-full h-9 rounded-md border border-[#e8e8e3] bg-white pl-8 pr-2 text-sm"
                        />
                    </div>
                    <select
                        value={cat}
                        onChange={(e) => setCat(e.target.value)}
                        data-testid="int-picker-category"
                        className="rounded-md border border-[#e8e8e3] bg-white px-2 h-9 text-sm"
                    >
                        <option value="recommended">Recommended for this role</option>
                        <option value="all">{`All (${allCount})`}</option>
                        {(catalog?.categories || []).map((c) => (
                            <option key={c.key} value={c.key}>{`${c.label} (${catCounts[c.key] || 0})`}</option>
                        ))}
                    </select>
                </div>

                {/* Grid */}
                <div className="flex-1 overflow-y-auto wfos-scrollbar p-4">
                    {filtered.length === 0 && (
                        <div className="text-sm text-gray-400 py-16 text-center">
                            No integrations match your filter.
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {filtered.map((i) => {
                            const isConnected = conSet.has(i.id);
                            return (
                                <button
                                    key={i.id}
                                    onClick={() => !isConnected && onSelect?.(i)}
                                    disabled={isConnected}
                                    data-testid={`int-picker-item-${i.id}`}
                                    className={`rounded-lg border p-3 flex items-start gap-3 text-left transition-colors ${
                                        isConnected
                                            ? "border-[#e8e8e3] bg-[#fafaf7] opacity-70 cursor-not-allowed"
                                            : "border-[#e8e8e3] bg-white hover:border-[#d6d6cf] hover:bg-[#fafaf7]"
                                    }`}
                                >
                                    <IntegrationLogo integration={i} size={32} rounded="rounded-lg" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5">
                                            <div className="text-[13px] font-semibold text-gray-900 truncate">{i.name}</div>
                                            {isConnected && (
                                                <span className="wfos-pill wfos-pill--green text-[9px]">
                                                    <Check className="w-2.5 h-2.5" strokeWidth={2.5} /> Connected
                                                </span>
                                            )}
                                            {!isConnected && recSet.has(i.id) && (
                                                <span className="wfos-pill wfos-pill--green text-[9px]">Recommended</span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-2 leading-snug">
                                            {i.description}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="px-6 py-3 border-t border-[#e8e8e3] text-[11px] text-gray-400">
                    {(catalog?.integrations || []).length} integrations across {(catalog?.categories || []).length} categories · mock OAuth · demo only
                </div>
            </div>
        </div>
    );
}
